import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ChevronDown, Copy, Pencil } from "lucide-react";
import Button from "../ui/Button";
import Tooltip from "../ui/Tooltip";
import PanelDeleteIconButton from "../draggable-panels/PanelDeleteIconButton";

/** Same bordered control style as Run / Build New in the behavior header. */
const TOOLBAR_BTN =
  "inline-flex shrink-0 items-center gap-1.5 px-3 py-1 text-xs border border-gray-700 rounded-md hover:border-gray-600 transition-colors duration-150 shadow-none w-max max-w-none";

function useClickOutside(ref, onOutside, enabled) {
  useEffect(() => {
    if (!enabled) return;
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      onOutside(e);
    };
    const onKey = (e) => {
      if (e.key === "Escape") onOutside(e);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref, onOutside, enabled]);
}

function BehaviorRow({
  behavior,
  selected,
  isUnavailable,
  onSelect,
  onDuplicate,
  onRename,
  onDelete,
  onAfterSelect,
}) {
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(behavior.name);
  const isTemplate = behavior.isTemplate === true;

  useEffect(() => {
    setName(behavior.name);
  }, [behavior.name]);

  const commit = () => {
    setEditing(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== behavior.name) onRename(trimmed);
    else setName(behavior.name);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (isUnavailable) return;
        onSelect(behavior.id);
        onAfterSelect?.();
      }}
      onKeyDown={(e) => {
        if (isUnavailable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(behavior.id);
          onAfterSelect?.();
        }
      }}
      className={`group relative flex items-center gap-2 rounded-sm px-2 py-1.5 text-xs transition-colors ${
        isUnavailable
          ? "cursor-not-allowed panel-text-helper bg-gray-900/40"
          : selected
            ? "cursor-pointer bg-gray-700/55 text-gray-50"
            : "cursor-pointer text-gray-200 hover:bg-gray-700/80"
      }`}
    >
      {editing ? (
        <input
          autoFocus={!isMobile}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setName(behavior.name);
              setEditing(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 rounded-sm bg-gray-900 border border-gray-600 px-1 py-0 text-xs text-gray-100 focus:outline-none focus:border-sky-500"
        />
      ) : (
        <Tooltip content={behavior.name} fullWidth>
          <span className="flex-1 min-w-0 truncate">{behavior.name}</span>
        </Tooltip>
      )}
      {isTemplate && !editing ? (
        <span className="text-[9px] uppercase tracking-wider panel-text-helper border border-gray-700 rounded-sm px-1 py-[1px]">
          tpl
        </span>
      ) : null}
      {isTemplate && isUnavailable ? (
        <span className="text-[9px] uppercase tracking-wider panel-text-helper border border-gray-800 rounded-sm px-1 py-[1px]">
          n/a
        </span>
      ) : null}
      {!editing ? (
        <div
          className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5"
          onClick={(e) => e.stopPropagation()}
        >
          {!isTemplate && !isUnavailable ? (
            <Tooltip content="Rename this behavior" side="right">
              <button
                type="button"
                aria-label="Rename"
                onClick={() => setEditing(true)}
                className="h-5 w-5 rounded-sm flex items-center justify-center text-gray-400 hover:bg-gray-700/80 hover:text-gray-200"
              >
                <Pencil className="w-3 h-3" />
              </button>
            </Tooltip>
          ) : null}
          <Tooltip
            content={isUnavailable ? "Template not available for this scene" : "Duplicate behavior"}
            side="right"
          >
            <button
              type="button"
              aria-label="Duplicate"
              onClick={() => {
                if (isUnavailable) return;
                onDuplicate();
                onAfterSelect?.();
              }}
              disabled={isUnavailable}
              className={`h-5 w-5 rounded-sm flex items-center justify-center ${
                isUnavailable
                  ? "panel-text-disabled cursor-not-allowed"
                  : "text-gray-400 hover:bg-gray-700/80 hover:text-gray-200"
              }`}
            >
              <Copy className="w-3 h-3" />
            </button>
          </Tooltip>
          {!isTemplate && !isUnavailable ? (
            <Tooltip content="Delete behavior" side="right">
              <PanelDeleteIconButton
                ariaLabel="Delete"
                onClick={() => {
                  onDelete();
                  onAfterSelect?.();
                }}
                className="h-5 w-5 rounded-sm p-0"
              />
            </Tooltip>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Header control: opens a scrollable list of behaviors (replaces the old left sidebar).
 */
export default function BehaviorPicker({
  behaviorList,
  unavailableTemplateIds = new Set(),
  selectedId,
  /** Shown in tooltip only; the header shows the editable name separately. */
  currentNameHint = "",
  onSelect,
  onDuplicate,
  onRename,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close, open);

  const titleHint =
    currentNameHint && currentNameHint.trim().length > 0
      ? `All behaviors · current: ${currentNameHint}`
      : "Open list of behaviors";

  return (
    <div ref={ref} className="relative flex shrink-0">
      <Button
        variant="secondary"
        size="small"
        title={titleHint}
        aria-label="Open behaviors list"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`${TOOLBAR_BTN} ${open ? "border-gray-600 bg-gray-800/50" : ""}`}
      >
        <span className="whitespace-nowrap text-gray-100">Behaviors</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </Button>
      {open ? (
        <div
          className="absolute left-0 top-full z-[10050] mt-1 w-72 max-h-[min(360px,70vh)] overflow-y-auto rounded-md border border-gray-600 p-1 shadow-xl"
          style={{ backgroundColor: "rgba(28, 28, 30, 0.98)", backdropFilter: "blur(6px)" }}
        >
          {behaviorList.length === 0 ? (
            <div className="px-2 py-2.5 text-[11px] panel-text-helper">No behaviors yet.</div>
          ) : (
            <div className="flex flex-col gap-px">
              {behaviorList.map((b) => (
                <BehaviorRow
                  key={b.id}
                  behavior={b}
                  selected={b.id === selectedId}
                  isUnavailable={b.isTemplate === true && unavailableTemplateIds.has(b.id)}
                  onSelect={onSelect}
                  onDuplicate={() => onDuplicate(b.id)}
                  onRename={(nextName) => onRename(b.id, nextName)}
                  onDelete={() => onDelete(b.id)}
                  onAfterSelect={close}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
